import { mutateByPath } from "../../../utility";
import { ArsTableBody, ArsTableGroup } from "../interface";
import { arst_checkSiblingSubgroups } from "./arst_checkSiblingSubgroups";

/**
 * Adds operations to the group(s) resolved from `targetDataPath` inside `bodyData`.
 *
 * When all sibling groups share a consistent sub-group structure the new operations
 * are applied to every sibling so the table stays consistent. Otherwise only the
 * directly targeted group is updated.
 *
 * @param bodyData - Current ArsTable body data
 * @param targetDataPath - JSON-Pointer path to the group that should receive the operations
 * @param newOperations - Operations to add (already converted to `ArsTableGroup` nodes)
 * @param isT - Type guard for the table's generic label type `T`
 * @param insertAt - Index at which to insert the operations; when `undefined` the
 *        operations are appended to the end of the group's existing sub-groups
 * @returns Updated body data, or `null` when the target group cannot be resolved
 */
export const arst_addOperationsToGroup = <T>(
  bodyData: ArsTableBody<T>,
  targetDataPath: string,
  newOperations: ArsTableGroup<T>[],
  isT: (v: unknown) => v is T,
  insertAt?: number,
): ArsTableBody<T> | null => {
  const targetGroups = arst_checkSiblingSubgroups(bodyData, targetDataPath, isT);
  if (!targetGroups) return null;

  let newBodyData = bodyData;
  Object.keys(targetGroups).forEach((key) => {
    const group = targetGroups[key];
    const currentSubGroups = group.subGroups ?? [];
    const newSubGroups =
      insertAt !== undefined
        ? [
            ...currentSubGroups.slice(0, insertAt),
            ...newOperations,
            ...currentSubGroups.slice(insertAt),
          ]
        : [...currentSubGroups, ...newOperations];
    newBodyData = mutateByPath(newBodyData, `${key}/subGroups`, newSubGroups);
  });

  return newBodyData;
};
